/**
 * SketchSpline - Offset Path / Brush Tool
 *
 * A SketchSpline is a path with an offset, creating a "brush" effect.
 *
 * Structure:
 * - centrePath: The path the user draws (center line)
 * - outlineLeft: Offset path on the left side
 * - outlineRight: Offset path on the right side
 * - offsetSize: Width/thickness of the brush
 *
 * This is ported from cc.sketchchair.sketch.SketchSpline.java
 */

import { SketchShape } from './SketchShape.js';
import { SketchPath } from './SketchPath.js';
import { SketchPoint } from './SketchPoint.js';
import { Vec2D } from './geometry/Vec2D.js';
import { ringSelfIntersects, unionRingsToPolygon } from '../geometry/PolygonOps.js';

export class SketchSpline extends SketchShape {
    // Cap types
    static CAP_ROUND = 1;
    static CAP_BUTT = 2;
    static CAP_SQUARE = 3;
    static CAP_PARALLEL = 4;
    static CAP_LEG = 5;

    // Join types
    static JOIN_BEVEL = 1;
    static JOIN_MITER = 2;
    static JOIN_ROUND = 3;

    // Max outside miter length as a multiple of the offset radius
    // (sharper corners fall back to bevel)
    static MITER_LIMIT = 4;

    // Max inside corner trim distance as a multiple of the offset radius.
    // The inside point is the true intersection of the offset lines, so this
    // only guards near-180-degree reversals from shooting to infinity.
    static INSIDE_TRIM_LIMIT = 10;

    // Offset types
    static OFFSET_LEFT = 0;
    static OFFSET_RIGHT = 1;
    static OFFSET_BOTH = 2;

    // Spline types
    static TYPE_LEG = 43;

    // Leg tool constants (taper ratios)
    static LEG_BRUSH_RATIO_TOP = 0.75;    // Top of leg is 75% of brush size
    static LEG_BRUSH_RATIO_BOTTOM = 0.5;  // Bottom of leg is 50% of brush size

    /**
     * Create a new SketchSpline
     * @param {SketchPlane} parentSketch - Parent sketch plane
     */
    constructor(parentSketch) {
        super(parentSketch);

        // Center path that user draws
        this.centrePath = new SketchPath(parentSketch);
        this.centrePath.setClosed(false);

        // Offset paths (calculated from center path)
        this.outlineLeft = [];
        this.outlineRight = [];

        // Combined outline path (for rendering/export)
        this.path = new SketchPath(parentSketch);

        // Settings
        this.offsetSize = 20; // Default brush width in pixels
        this.offsetType = SketchSpline.OFFSET_BOTH;
        this.capType = SketchSpline.CAP_ROUND;
        this.joinType = SketchSpline.JOIN_BEVEL;
        this.type = null; // Spline type (e.g., TYPE_LEG)

        // Variable offset sizes per point
        this.centreOffset = new Map(); // pointIndex -> offsetSize

        // State
        this.isBuilt = false;
        this.selected = false;

        // Selection state
        this.selectedNodes = []; // Array of selected SketchPoint references

        // Settings
        this.autoSmooth = true; // Auto-optimize path after editing
        this.splineMoveFalloff = 220; // Falloff radius in pixels (wide = smoother drags)
        this.splinePointEvery = 10; // Point spacing for optimization
    }

    /**
     * Add a point to the center path
     * @param {SketchPoint} point - Point to add
     */
    add(point) {
        this.centrePath.add(point);
        this.isBuilt = false; // Mark for rebuild
    }

    /**
     * Get number of points in center path
     * @returns {number} Number of points
     */
    size() {
        return this.centrePath.size();
    }

    /**
     * Build/rebuild the offset paths
     * Calculates left and right outlines from center path
     */
    build() {
        this.offset();
        this.buildPath();
        this.isBuilt = true;
    }

    /**
     * Calculate offset paths from center path
     * Core algorithm that creates the "brush" effect.
     *
     * Each interior vertex is classified per side as an INSIDE or OUTSIDE
     * corner (via the turn direction). Inside corners get a single trimmed
     * point on the angle bisector (the intersection of the two offset
     * lines); outside corners get a proper join: round arc, miter with a
     * miter limit, or bevel. The previous implementation used only the
     * forward segment's perpendicular at corners, which made the outline
     * flip sides at acute angles.
     */
    offset() {
        if (this.centrePath.size() < 2) {
            return;
        }

        // Clear existing offset paths
        this.outlineLeft = [];
        this.outlineRight = [];

        // Sample the centre path: bezier segments (from bezier-tool editing)
        // are subdivided so the offset outline follows the CURVE, not the
        // straight chord between control points. Each sample carries an
        // interpolated radius (legs taper via per-point radii).
        const samples = this.sampleCentrePath();
        const n = samples.length;
        if (n < 2) return;

        const pts = samples.map(s => new Vec2D(s.x, s.y));

        // Unit direction of each segment; zero-length segments (duplicate
        // points) reuse the previous direction to avoid NaN normals
        const dirs = [];
        let lastDir = new Vec2D(1, 0);
        for (let i = 0; i < n - 1; i++) {
            const dx = pts[i + 1].x - pts[i].x;
            const dy = pts[i + 1].y - pts[i].y;
            const len = Math.hypot(dx, dy);
            if (len > 1e-6) {
                lastDir = new Vec2D(dx / len, dy / len);
            }
            dirs.push(lastDir);
        }

        // Left normal of a direction (rotate +90 degrees)
        const perp = (v) => new Vec2D(-v.y, v.x);

        for (let i = 0; i < n; i++) {
            const cur = pts[i];

            // Radius interpolated at sample time (varies per point for legs)
            const radius = samples[i].r;

            const leftPts = [];
            const rightPts = [];

            if (i === 0 || i === n - 1) {
                // Endpoints: single perpendicular from the only adjacent segment
                const m = perp(dirs[i === 0 ? 0 : n - 2]);
                leftPts.push(cur.add(m.scale(radius)));
                rightPts.push(cur.add(m.scale(-radius)));
            } else {
                const v1 = dirs[i - 1];
                const v2 = dirs[i];
                const cross = v1.x * v2.y - v1.y * v2.x;
                const m1 = perp(v1);
                const m2 = perp(v2);

                // Left turn (cross > 0): left side is INSIDE the corner
                this.buildCornerPoints(cur, m1, m2, radius, +1, cross > 1e-9, leftPts);
                this.buildCornerPoints(cur, m1, m2, radius, -1, cross < -1e-9, rightPts);
            }

            // Emit according to offset type (skipping consecutive duplicates -
            // collinear vertices produce identical join points)
            const pushUnique = (arr, p) => {
                const last = arr[arr.length - 1];
                if (last && Math.abs(last.x - p.x) < 1e-9 && Math.abs(last.y - p.y) < 1e-9) return;
                arr.push(new SketchPoint(p.x, p.y));
            };
            if (this.offsetType === SketchSpline.OFFSET_BOTH) {
                leftPts.forEach(p => pushUnique(this.outlineLeft, p));
                rightPts.forEach(p => pushUnique(this.outlineRight, p));
            } else if (this.offsetType === SketchSpline.OFFSET_LEFT) {
                leftPts.forEach(p => this.outlineLeft.push(new SketchPoint(p.x, p.y)));
                this.outlineRight.push(new SketchPoint(cur.x, cur.y));
            } else if (this.offsetType === SketchSpline.OFFSET_RIGHT) {
                this.outlineLeft.push(new SketchPoint(cur.x, cur.y));
                rightPts.forEach(p => this.outlineRight.push(new SketchPoint(p.x, p.y)));
            }
        }

        // Remove small backtracking loops: when a path segment is shorter
        // than the adjacent corner trims consume, the two inside trim points
        // cross each other and leave a tiny step/loop in the joint.
        this.removeSideLoops(this.outlineLeft);
        this.removeSideLoops(this.outlineRight);
    }

    /**
     * Collapse local self-intersection loops in one outline side by
     * replacing the crossing span with the intersection point (classic
     * offset-polyline cleanup for segments shorter than their trims).
     * @param {Array<SketchPoint>} pts - Outline side, modified in place
     */
    removeSideLoops(pts) {
        const intersect = (a, b, c, d) => {
            const d1x = b.x - a.x, d1y = b.y - a.y;
            const d2x = d.x - c.x, d2y = d.y - c.y;
            const denom = d1x * d2y - d1y * d2x;
            if (Math.abs(denom) < 1e-12) return null;
            const t = ((c.x - a.x) * d2y - (c.y - a.y) * d2x) / denom;
            const u = ((c.x - a.x) * d1y - (c.y - a.y) * d1x) / denom;
            const EPS = 1e-9;
            if (t <= EPS || t >= 1 - EPS || u <= EPS || u >= 1 - EPS) return null;
            return { x: a.x + t * d1x, y: a.y + t * d1y };
        };

        let guard = 0;
        let changed = true;
        while (changed && guard++ < 20) {
            changed = false;
            outer:
            for (let i = 0; i + 1 < pts.length; i++) {
                const maxJ = Math.min(i + 10, pts.length - 1);
                for (let j = i + 2; j < maxJ; j++) {
                    const X = intersect(pts[i], pts[i + 1], pts[j], pts[j + 1]);
                    if (X) {
                        pts.splice(i + 1, j - i, new SketchPoint(X.x, X.y));
                        changed = true;
                        break outer;
                    }
                }
            }
        }
    }

    /**
     * Sample the centre path into a dense polyline for offsetting.
     * Straight segments pass through unchanged; bezier segments (start
     * point's controlPoint2 / end point's controlPoint1) are subdivided.
     * Each sample carries the interpolated offset radius.
     * @returns {Array<{x, y, r}>}
     */
    sampleCentrePath() {
        const points = this.centrePath.points;
        const n = points.length;
        const radiusAt = (i) => this.centreOffset.has(i) ? this.centreOffset.get(i) : this.offsetSize;

        const samples = [];
        for (let i = 0; i < n; i++) {
            const cur = points[i];
            const r1 = radiusAt(i);
            samples.push({ x: cur.x, y: cur.y, r: r1 });

            if (i === n - 1) break;

            const next = points[i + 1];
            const cp1 = cur.controlPoint2;
            const cp2 = next.controlPoint1;
            if (cp1 || cp2) {
                const c1 = cp1 || cur;
                const c2 = cp2 || next;
                const r2 = radiusAt(i + 1);
                const STEPS = 12;
                for (let s = 1; s < STEPS; s++) {
                    const t = s / STEPS;
                    const t1 = 1 - t;
                    samples.push({
                        x: t1 * t1 * t1 * cur.x + 3 * t1 * t1 * t * c1.x + 3 * t1 * t * t * c2.x + t * t * t * next.x,
                        y: t1 * t1 * t1 * cur.y + 3 * t1 * t1 * t * c1.y + 3 * t1 * t * t * c2.y + t * t * t * next.y,
                        r: r1 + (r2 - r1) * t
                    });
                }
            }
        }
        return samples;
    }

    /**
     * Build the offset point(s) for one side of an interior corner.
     *
     * @param {Vec2D} cur - The corner vertex
     * @param {Vec2D} m1 - Unit LEFT normal of the incoming segment
     * @param {Vec2D} m2 - Unit LEFT normal of the outgoing segment
     * @param {number} radius - Offset distance at this vertex
     * @param {number} sign - +1 for the left side, -1 for the right side
     * @param {boolean} isInside - Whether this side is inside the corner
     * @param {Array<Vec2D>} out - Receives the generated point(s)
     */
    buildCornerPoints(cur, m1, m2, radius, sign, isInside, out) {
        // Unit offset directions for this side
        const o1 = new Vec2D(m1.x * sign, m1.y * sign);
        const o2 = new Vec2D(m2.x * sign, m2.y * sign);

        const sumX = o1.x + o2.x;
        const sumY = o1.y + o2.y;
        const sumLen = Math.hypot(sumX, sumY);

        if (sumLen < 1e-6) {
            // Path reverses 180 degrees: no bisector exists - emit both
            // perpendicular points (a hairpin)
            out.push(cur.add(o1.scale(radius)));
            out.push(cur.add(o2.scale(radius)));
            return;
        }

        // Angle bisector and cosine of the half-angle between o1 and o2.
        // The intersection of the two offset lines lies on the bisector at
        // distance radius / cosHalf from the vertex (both inside and outside).
        const bis = new Vec2D(sumX / sumLen, sumY / sumLen);
        const cosHalf = bis.x * o1.x + bis.y * o1.y;

        if (isInside) {
            // Trimmed corner: single point where the two offset lines meet.
            // Clamp so near-reversals can't shoot the point to infinity.
            const dist = radius / Math.max(cosHalf, 1 / SketchSpline.INSIDE_TRIM_LIMIT);
            out.push(cur.add(bis.scale(dist)));
            return;
        }

        // Outside of the corner: apply the join style
        if (this.joinType === SketchSpline.JOIN_MITER) {
            const miterScale = 1 / Math.max(cosHalf, 1e-6);
            if (miterScale <= SketchSpline.MITER_LIMIT) {
                out.push(cur.add(bis.scale(radius * miterScale)));
                return;
            }
            // Miter limit exceeded - fall through to bevel
        }

        if (this.joinType === SketchSpline.JOIN_ROUND) {
            // Arc from o1 to o2 around the vertex (short way, on the outside)
            const a1 = Math.atan2(o1.y, o1.x);
            const a2 = Math.atan2(o2.y, o2.x);
            let sweep = a2 - a1;
            while (sweep > Math.PI) sweep -= Math.PI * 2;
            while (sweep < -Math.PI) sweep += Math.PI * 2;

            const ARC_STEP = Math.PI / 12; // ~15 degrees per segment
            const steps = Math.max(1, Math.ceil(Math.abs(sweep) / ARC_STEP));
            for (let s = 0; s <= steps; s++) {
                const a = a1 + sweep * (s / steps);
                out.push(new Vec2D(
                    cur.x + Math.cos(a) * radius,
                    cur.y + Math.sin(a) * radius
                ));
            }
            return;
        }

        // JOIN_BEVEL (and miter-limit fallback): both perpendicular points
        out.push(cur.add(o1.scale(radius)));
        out.push(cur.add(o2.scale(radius)));
    }

    /**
     * Find intersection point between two lines
     * @param {Vec2D} p1 - Line 1 start
     * @param {Vec2D} p2 - Line 1 end
     * @param {Vec2D} p3 - Line 2 start
     * @param {Vec2D} p4 - Line 2 end
     * @returns {Vec2D|null} Intersection point or null
     */
    lineIntersection(p1, p2, p3, p4) {
        const x1 = p1.x, y1 = p1.y;
        const x2 = p2.x, y2 = p2.y;
        const x3 = p3.x, y3 = p3.y;
        const x4 = p4.x, y4 = p4.y;

        const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

        if (Math.abs(denom) < 0.0001) {
            return null; // Lines are parallel
        }

        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

        return new Vec2D(
            x1 + t * (x2 - x1),
            y1 + t * (y2 - y1)
        );
    }

    /**
     * Build the combined path from offset outlines
     * Creates a closed path: left outline + cap + right outline + cap
     */
    buildPath() {
        // Create new path
        this.path = new SketchPath(this.getParentSketch());

        if (this.outlineLeft.length === 0 || this.outlineRight.length === 0) {
            return;
        }

        // Add left outline
        for (let i = 0; i < this.outlineLeft.length; i++) {
            this.path.add(this.outlineLeft[i]);
        }

        // Add end cap
        this.addEndCap(
            this.outlineLeft[this.outlineLeft.length - 1],
            this.outlineRight[this.outlineRight.length - 1],
            this.centrePath.points[this.centrePath.points.length - 1]
        );

        // Add right outline (reversed)
        for (let i = this.outlineRight.length - 1; i >= 0; i--) {
            this.path.add(this.outlineRight[i]);
        }

        // Add start cap
        this.addStartCap(
            this.outlineRight[0],
            this.outlineLeft[0],
            this.centrePath.points[0]
        );

        this.path.setClosed(true);

        // At extreme corners (offset larger than the local segment lengths)
        // the O(n) join pass can still produce a self-intersecting outline.
        // Detect that and rebuild the stroke exactly as the boolean union of
        // per-segment quads and vertex discs - each input is simple and
        // convex, so the union is always well-defined.
        //
        // The union can legitimately contain HOLES: a closed brush stroke
        // (start meeting end - a drawn ring/donut) always self-intersects
        // where the end caps overlap the start of the stroke, and its union
        // is an annulus. The hole (the stroke's inner outline) is preserved
        // on path.holes; discarding it silently turned rings into discs
        // (the "missing points" bug: the whole inner profile vanished from
        // both the 2D cut pattern and the 3D meshes).
        const ring = this.path.points.map(p => ({ x: p.x, y: p.y }));
        if (ring.length >= 4 && ringSelfIntersects(ring)) {
            const exact = this.buildOutlineByUnion();
            if (exact && exact.outer.length >= 3) {
                const cleanPath = new SketchPath(this.getParentSketch());
                for (const p of exact.outer) {
                    cleanPath.add(new SketchPoint(p.x, p.y));
                }
                cleanPath.setClosed(true);
                cleanPath.holes = exact.holes.map(hole =>
                    hole.map(p => new SketchPoint(p.x, p.y)));
                this.path = cleanPath;
            }
        }
    }

    /**
     * Build the stroke outline as the boolean union of one quad per segment
     * plus a disc at every vertex. Robust at ANY corner angle (used as the
     * fallback when the fast join-based outline self-intersects).
     *
     * Works on the SAMPLED centre path (bezier segments subdivided, radii
     * interpolated), matching offset() - using the raw control points here
     * flattened bezier-edited strokes into straight chords.
     *
     * Discs give round joins everywhere; end discs are only added for round
     * caps so butt/parallel ends stay flat.
     *
     * @returns {{outer: Array<{x,y}>, holes: Array<Array<{x,y}>>}|null}
     *          Outline polygon (holes preserved - a closed stroke unions
     *          into an annulus), or null on failure
     */
    buildOutlineByUnion() {
        const samples = this.sampleCentrePath();
        const n = samples.length;
        if (n < 2) return null;

        const rings = [];
        const DISC_SEGMENTS = 24;

        const disc = (cx, cy, r) => {
            const ring = [];
            for (let s = 0; s < DISC_SEGMENTS; s++) {
                const a = (s / DISC_SEGMENTS) * Math.PI * 2;
                ring.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
            }
            return ring;
        };

        // One quad (trapezoid for varying radius) per segment
        for (let i = 0; i < n - 1; i++) {
            const p1 = samples[i];
            const p2 = samples[i + 1];
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const len = Math.hypot(dx, dy);
            if (len < 1e-6) continue;

            const mx = -dy / len; // unit left normal
            const my = dx / len;
            const r1 = p1.r;
            const r2 = p2.r;

            rings.push([
                { x: p1.x + mx * r1, y: p1.y + my * r1 },
                { x: p2.x + mx * r2, y: p2.y + my * r2 },
                { x: p2.x - mx * r2, y: p2.y - my * r2 },
                { x: p1.x - mx * r1, y: p1.y - my * r1 }
            ]);
        }

        if (rings.length === 0) return null;

        // Discs at interior vertices (round joins at any angle)
        for (let i = 1; i < n - 1; i++) {
            rings.push(disc(samples[i].x, samples[i].y, samples[i].r));
        }

        // End discs for round-ish caps
        if (this.capType === SketchSpline.CAP_ROUND || this.capType === SketchSpline.CAP_LEG) {
            rings.push(disc(samples[0].x, samples[0].y, samples[0].r));
            rings.push(disc(samples[n - 1].x, samples[n - 1].y, samples[n - 1].r));
        }

        return unionRingsToPolygon(rings);
    }

    /**
     * Add cap at the start of the path
     * @param {SketchPoint} right - Right outline point
     * @param {SketchPoint} left - Left outline point
     * @param {SketchPoint} center - Center path point
     */
    /**
     * Choose the round-cap arc sweep whose MIDPOINT faces outwardAngle
     * (away from the path). The two corner angles are ~PI apart, so
     * "pick the larger arc" is a knife-edge that flips with floating-point
     * noise at certain stroke angles, inverting the cap into the stroke.
     */
    roundCapSweep(startAngle, endAngle, outwardAngle) {
        let d1 = endAngle - startAngle;
        if (d1 < 0) d1 += 2 * Math.PI;
        const d2 = d1 - 2 * Math.PI;

        const angDist = (a, b) => {
            let d = Math.abs(a - b) % (2 * Math.PI);
            return d > Math.PI ? 2 * Math.PI - d : d;
        };
        const mid1 = startAngle + d1 / 2;
        const mid2 = startAngle + d2 / 2;
        return angDist(mid1, outwardAngle) <= angDist(mid2, outwardAngle) ? d1 : d2;
    }

    addStartCap(right, left, center) {
        if (this.capType === SketchSpline.CAP_ROUND) {
            // Add circular arc from right to left
            const steps = 8;
            const radius = Math.sqrt(
                (right.x - center.x) ** 2 + (right.y - center.y) ** 2
            );

            // Calculate start and end angles
            const startAngle = Math.atan2(right.y - center.y, right.x - center.x);
            const endAngle = Math.atan2(left.y - center.y, left.x - center.x);

            // Outward direction at the START of the path, from the SAMPLED
            // centre path (bezier-aware): the raw first chord points the
            // wrong way on curved ends, skewing the cap. The cap's chord
            // must be the perpendicular bisector of the actual incoming
            // tangent.
            let outwardAngle = startAngle + Math.PI / 2; // fallback
            const startSamples = this.sampleCentrePath();
            if (startSamples.length >= 2) {
                const s0 = startSamples[0];
                const s1 = startSamples[1];
                outwardAngle = Math.atan2(s0.y - s1.y, s0.x - s1.x);
            }

            const angleDiff = this.roundCapSweep(startAngle, endAngle, outwardAngle);

            for (let i = 1; i < steps; i++) {
                const t = i / steps;
                const angle = startAngle + angleDiff * t;
                const x = center.x + radius * Math.cos(angle);
                const y = center.y + radius * Math.sin(angle);
                this.path.add(new SketchPoint(x, y));
            }
        } else if (this.capType === SketchSpline.CAP_SQUARE) {
            // Extend perpendicular to create square cap
            const dx = left.x - right.x;
            const dy = left.y - right.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len > 0) {
                const perpX = -dy / len * (len / 2);
                const perpY = dx / len * (len / 2);

                this.path.add(new SketchPoint(right.x + perpX, right.y + perpY));
                this.path.add(new SketchPoint(left.x + perpX, left.y + perpY));
            }
        } else if (this.capType === SketchSpline.CAP_PARALLEL) {
            // Just connect with a straight line (no additional points needed)
            // The outline points will connect directly
        } else if (this.capType === SketchSpline.CAP_LEG) {
            // Attachment end of the leg: a TRUE CIRCULAR cap of radius =
            // the leg's top radius, exactly like CAP_ROUND. (The previous
            // sine-bulge peaked at 1.5x the radius, rendering the top as an
            // oval that extended far past the actual leg width.)
            const steps = 8;
            const radius = Math.sqrt(
                (right.x - center.x) ** 2 + (right.y - center.y) ** 2
            );

            const startAngle = Math.atan2(right.y - center.y, right.x - center.x);
            const endAngle = Math.atan2(left.y - center.y, left.x - center.x);

            // Outward direction at the START of the path (away from the
            // foot), from the sampled centre path - see CAP_ROUND above
            let outwardAngle = startAngle + Math.PI / 2; // fallback
            const startSamples = this.sampleCentrePath();
            if (startSamples.length >= 2) {
                const s0 = startSamples[0];
                const s1 = startSamples[1];
                outwardAngle = Math.atan2(s0.y - s1.y, s0.x - s1.x);
            }

            const angleDiff = this.roundCapSweep(startAngle, endAngle, outwardAngle);

            for (let i = 1; i < steps; i++) {
                const t = i / steps;
                const angle = startAngle + angleDiff * t;
                this.path.add(new SketchPoint(
                    center.x + radius * Math.cos(angle),
                    center.y + radius * Math.sin(angle)
                ));
            }
        }
        // CAP_BUTT is default - just straight line, no additional points
    }

    /**
     * Add cap at the end of the path
     * @param {SketchPoint} left - Left outline point
     * @param {SketchPoint} right - Right outline point
     * @param {SketchPoint} center - Center path point
     */
    addEndCap(left, right, center) {
        if (this.capType === SketchSpline.CAP_ROUND) {
            // Add circular arc from left to right
            const steps = 8;
            const radius = Math.sqrt(
                (left.x - center.x) ** 2 + (left.y - center.y) ** 2
            );

            // Calculate start and end angles
            const startAngle = Math.atan2(left.y - center.y, left.x - center.x);
            const endAngle = Math.atan2(right.y - center.y, right.x - center.x);

            // Outward direction at the END of the path, from the SAMPLED
            // centre path (bezier-aware) - see addStartCap
            let outwardAngle = startAngle - Math.PI / 2; // fallback
            const endSamples = this.sampleCentrePath();
            if (endSamples.length >= 2) {
                const sPrev = endSamples[endSamples.length - 2];
                const sEnd = endSamples[endSamples.length - 1];
                outwardAngle = Math.atan2(sEnd.y - sPrev.y, sEnd.x - sPrev.x);
            }

            const angleDiff = this.roundCapSweep(startAngle, endAngle, outwardAngle);

            for (let i = 1; i < steps; i++) {
                const t = i / steps;
                const angle = startAngle + angleDiff * t;
                const x = center.x + radius * Math.cos(angle);
                const y = center.y + radius * Math.sin(angle);
                this.path.add(new SketchPoint(x, y));
            }
        } else if (this.capType === SketchSpline.CAP_SQUARE) {
            // Extend perpendicular to create square cap
            const dx = right.x - left.x;
            const dy = right.y - left.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len > 0) {
                const perpX = -dy / len * (len / 2);
                const perpY = dx / len * (len / 2);

                this.path.add(new SketchPoint(left.x + perpX, left.y + perpY));
                this.path.add(new SketchPoint(right.x + perpX, right.y + perpY));
            }
        } else if (this.capType === SketchSpline.CAP_PARALLEL) {
            // Just connect with a straight line (no additional points needed)
            // The outline points will connect directly
        } else if (this.capType === SketchSpline.CAP_LEG) {
            // Ground-parallel cap (bottom of leg): the foot is a horizontal
            // cut through the foot centre. Each corner is the EXACT
            // intersection of the horizontal line y = center.y with that
            // side's outline edge (the line through the last left/right
            // outline point along the leg direction). The old construction
            // (symmetric +-hypotenuse about the centre with a proximity
            // flip) was only correct for symmetric vertical legs - on
            // slanted and especially HORIZONTAL legs one side edge extended
            // past the cap, leaving a protruding sliver.

            // Leg direction at the end, from the sampled centre path
            let legDir = new Vec2D(0, 1);
            const endSamples = this.sampleCentrePath();
            if (endSamples.length >= 2) {
                const sPrev = endSamples[endSamples.length - 2];
                const sEnd = endSamples[endSamples.length - 1];
                const d = new Vec2D(sEnd.x - sPrev.x, sEnd.y - sPrev.y);
                if (d.magnitude() > 1e-6) legDir = d.normalize();
            }

            // A ground-parallel foot only exists when the leg points
            // meaningfully up/down. A (near-)HORIZONTAL leg's side edges
            // are (near-)parallel to the ground line - no intersection -
            // so it gets a flat butt cap (outline closes left->right
            // directly, perpendicular to the leg).
            if (Math.abs(legDir.y) > 0.35) {
                // Cap overhang safety: never extend a corner further along
                // the ground than the leg is long
                let maxOverhang = Infinity;
                if (this.centrePath.points.length >= 2) {
                    const prevCenter = this.centrePath.points[this.centrePath.points.length - 2];
                    maxOverhang = Math.hypot(center.x - prevCenter.x, center.y - prevCenter.y);
                }
                const cornerX = (edgePoint) => {
                    const t = (center.y - edgePoint.y) / legDir.y;
                    const x = edgePoint.x + legDir.x * t;
                    return Math.min(center.x + maxOverhang, Math.max(center.x - maxOverhang, x));
                };

                // TRIM the side edges onto the ground line: move each
                // side's LAST outline point (the perpendicular foot point,
                // which on a slanted leg dips BELOW the ground plane) to
                // its exact edge/ground intersection. The cap is then the
                // straight segment between the two moved endpoints -
                // appending corners while keeping the perpendicular points
                // left the downhill edge protruding past the cap.
                left.x = cornerX(left); left.y = center.y;
                right.x = cornerX(right); right.y = center.y;
            }
            // else: butt cap - no extra points
        }
        // CAP_BUTT is default - just straight line, no additional points
    }

    /**
     * Get the center path
     * @returns {SketchPath} Center path
     */
    getCentrePath() {
        return this.centrePath;
    }

    /**
     * Get the combined outline path
     * @returns {SketchPath} Outline path
     */
    getPath() {
        return this.path;
    }

    /**
     * Set offset size
     * @param {number} size - Offset size in pixels
     */
    setOffsetSize(size) {
        this.offsetSize = size;
        this.isBuilt = false;
    }

    /**
     * Get offset size
     * @returns {number} Offset size
     */
    getOffsetSize() {
        return this.offsetSize;
    }

    /**
     * Set spline type
     * @param {number} type - Spline type (e.g., TYPE_LEG)
     */
    setType(type) {
        this.type = type;
        this.isBuilt = false;
    }

    /**
     * Get spline type
     * @returns {number} Spline type
     */
    getType() {
        return this.type;
    }

    /**
     * Get index of a point in the center path
     * @param {SketchPoint} point - Point to find
     * @returns {number} Index or -1 if not found
     */
    getIndex(point) {
        return this.centrePath.points.indexOf(point);
    }

    /**
     * Get distance between two points along the curve
     * @param {SketchPoint} p1 - First point
     * @param {SketchPoint} p2 - Second point
     * @returns {number} Distance along curve
     */
    getDistBetween(p1, p2) {
        const idx1 = this.getIndex(p1);
        const idx2 = this.getIndex(p2);

        if (idx1 === -1 || idx2 === -1) {
            // Fall back to straight-line distance
            return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
        }

        // Calculate distance along the path
        let dist = 0;
        const start = Math.min(idx1, idx2);
        const end = Math.max(idx1, idx2);

        for (let i = start; i < end; i++) {
            const a = this.centrePath.points[i];
            const b = this.centrePath.points[i + 1];
            dist += Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
        }

        return dist;
    }

    /**
     * Move a point with falloff/smoothing effect (rubber-like behavior)
     * @param {SketchPoint} selectVec - Selected point to move
     * @param {Vec2D} pointOnPlane - Target position
     */
    movePointFalloff(selectVec, pointOnPlane) {
        // Offset paths are deliberately clicked points: move ONLY the grabbed
        // point, exactly to the cursor - no rubber-band falloff and no
        // automatic point insertion/removal (those are freehand behaviours).
        // Legs get the same 1:1 treatment: they are exactly two deliberate
        // endpoints, and the falloff path below bails out for size <= 2,
        // which made leg points un-draggable with the select tool.
        if (this.type === SketchShape.OFFSET_SPLINE || this.type === SketchSpline.TYPE_LEG) {
            const dx = pointOnPlane.x - selectVec.x;
            const dy = pointOnPlane.y - selectVec.y;
            selectVec.x = pointOnPlane.x;
            selectVec.y = pointOnPlane.y;
            // Bezier handles travel with their anchor
            if (selectVec.controlPoint1) { selectVec.controlPoint1.x += dx; selectVec.controlPoint1.y += dy; }
            if (selectVec.controlPoint2) { selectVec.controlPoint2.x += dx; selectVec.controlPoint2.y += dy; }
            this.isBuilt = false;
            return;
        }

        if (this.centrePath.size() <= 2) {
            return;
        }

        const diff = new Vec2D(
            pointOnPlane.x - selectVec.x,
            pointOnPlane.y - selectVec.y
        );

        const pointIndex = this.getIndex(selectVec);

        if (pointIndex === 0) {
            // First point: move directly and slightly affect second point
            selectVec.x = pointOnPlane.x;
            selectVec.y = pointOnPlane.y;

            if (this.centrePath.size() > 1) {
                const next = this.centrePath.points[1];
                const scaledDiff = diff.scale(0.2);
                next.x += scaledDiff.x;
                next.y += scaledDiff.y;

                // Check if we should add a point (extending)
                const distToNext = Math.sqrt(
                    (selectVec.x - next.x) ** 2 + (selectVec.y - next.y) ** 2
                );
                const addPointThreshold = 30; // Add point if distance exceeds this

                if (distToNext > addPointThreshold) {
                    // Add a new point between the end point and second point
                    const newPoint = new SketchPoint(
                        (selectVec.x + next.x) / 2,
                        (selectVec.y + next.y) / 2
                    );
                    this.centrePath.points.splice(1, 0, newPoint);
                    // Update selectedNodes reference if needed
                    const selectedIndex = this.selectedNodes.indexOf(selectVec);
                    if (selectedIndex !== -1) {
                        // Point index has shifted, but the reference is still valid
                    }
                }

                // Check if we should remove a point (retracting)
                if (this.centrePath.size() > 3) {
                    const removePointThreshold = 15; // Remove next point if we get this close
                    if (distToNext < removePointThreshold) {
                        // Remove the second point and merge into the first
                        this.centrePath.points.splice(1, 1);
                        // Update selected nodes
                        const nextIndex = this.selectedNodes.indexOf(next);
                        if (nextIndex !== -1) {
                            this.selectedNodes.splice(nextIndex, 1);
                        }
                    }
                }
            }
        } else if (pointIndex === this.centrePath.size() - 1) {
            // Last point: move directly and slightly affect second-to-last
            selectVec.x = pointOnPlane.x;
            selectVec.y = pointOnPlane.y;

            if (this.centrePath.size() > 1) {
                const prev = this.centrePath.points[this.centrePath.size() - 2];
                const scaledDiff = diff.scale(0.2);
                prev.x += scaledDiff.x;
                prev.y += scaledDiff.y;

                // Check if we should add a point (extending)
                const distToPrev = Math.sqrt(
                    (selectVec.x - prev.x) ** 2 + (selectVec.y - prev.y) ** 2
                );
                const addPointThreshold = 30;

                if (distToPrev > addPointThreshold) {
                    // Add a new point between the end point and second-to-last point
                    const newPoint = new SketchPoint(
                        (selectVec.x + prev.x) / 2,
                        (selectVec.y + prev.y) / 2
                    );
                    this.centrePath.points.splice(this.centrePath.size() - 1, 0, newPoint);
                }

                // Check if we should remove a point (retracting)
                if (this.centrePath.size() > 3) {
                    const removePointThreshold = 15;
                    if (distToPrev < removePointThreshold) {
                        // Remove the second-to-last point
                        this.centrePath.points.splice(this.centrePath.size() - 2, 1);
                        // Update selected nodes
                        const prevIndex = this.selectedNodes.indexOf(prev);
                        if (prevIndex !== -1) {
                            this.selectedNodes.splice(prevIndex, 1);
                        }
                    }
                }
            }
        } else {
            // Middle point: apply falloff to neighboring points
            for (let i = 0; i < this.centrePath.size(); i++) {
                const v = this.centrePath.points[i];

                const dist = this.getDistBetween(v, selectVec);
                const fieldDia = this.splineMoveFalloff; // Note: Would divide by zoom if we had it
                const delta = (fieldDia - dist) / fieldDia;

                if (dist < fieldDia) {
                    // Sinusoidal falloff for smooth rubber-like effect
                    const scale = Math.sin((Math.PI / 2) * delta);
                    const shapedDiff = diff.scale(scale);

                    v.x += shapedDiff.x;
                    v.y += shapedDiff.y;
                    if (v.controlPoint1) { v.controlPoint1.x += shapedDiff.x; v.controlPoint1.y += shapedDiff.y; }
                    if (v.controlPoint2) { v.controlPoint2.x += shapedDiff.x; v.controlPoint2.y += shapedDiff.y; }
                }
            }
        }

        // Mark for rebuild
        this.isBuilt = false;
    }

    /**
     * Get total length of the center path
     * @returns {number} Total length in pixels
     */
    getLength() {
        let length = 0;
        for (let i = 0; i < this.centrePath.size() - 1; i++) {
            const a = this.centrePath.points[i];
            const b = this.centrePath.points[i + 1];
            length += Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
        }
        return length;
    }

    /**
     * Get position along the spline at parameter t (0 to 1)
     * @param {number} t - Parameter from 0 to 1
     * @returns {Vec2D} Position at t
     */
    getPos(t) {
        if (this.centrePath.size() === 0) {
            return new Vec2D(0, 0);
        }

        if (t <= 0) {
            const p = this.centrePath.points[0];
            return new Vec2D(p.x, p.y);
        }

        if (t >= 1) {
            const p = this.centrePath.points[this.centrePath.size() - 1];
            return new Vec2D(p.x, p.y);
        }

        // Find segment at parameter t based on cumulative length
        const totalLength = this.getLength();
        const targetDist = t * totalLength;

        let currentDist = 0;
        for (let i = 0; i < this.centrePath.size() - 1; i++) {
            const a = this.centrePath.points[i];
            const b = this.centrePath.points[i + 1];
            const segmentLength = Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

            if (currentDist + segmentLength >= targetDist) {
                // Interpolate within this segment
                const localT = (targetDist - currentDist) / segmentLength;
                return new Vec2D(
                    a.x + (b.x - a.x) * localT,
                    a.y + (b.y - a.y) * localT
                );
            }

            currentDist += segmentLength;
        }

        // Fallback to last point
        const p = this.centrePath.points[this.centrePath.size() - 1];
        return new Vec2D(p.x, p.y);
    }

    /**
     * Optimize/resample the path to have evenly spaced points
     * Called after editing to smooth out the curve
     */
    optimize() {
        if (!this.autoSmooth) {
            return;
        }

        if (this.centrePath.size() < 3) {
            return;
        }

        // Preserve first point
        const lastStoredPoint = this.centrePath.points[0];
        const optimizedArray = [lastStoredPoint];

        // Resample at regular intervals
        const totalLength = this.getLength();
        const step = this.splinePointEvery / totalLength;

        for (let i = step; i < 1; i += step) {
            const pos = this.getPos(i);
            optimizedArray.push(new SketchPoint(pos.x, pos.y));
        }

        // Add last point
        optimizedArray.push(this.centrePath.points[this.centrePath.size() - 1]);

        // Replace path with optimized version
        this.centrePath.points = optimizedArray;

        // Preserve selected nodes by finding nearest points
        const newSelectedNodes = [];
        for (const selectedNode of this.selectedNodes) {
            let nearestIndex = 0;
            let nearestDist = Infinity;

            for (let i = 0; i < this.centrePath.points.length; i++) {
                const p = this.centrePath.points[i];
                const dist = Math.sqrt(
                    (p.x - selectedNode.x) ** 2 + (p.y - selectedNode.y) ** 2
                );
                if (dist < nearestDist) {
                    nearestDist = dist;
                    nearestIndex = i;
                }
            }

            // Replace nearest point with selected node
            this.centrePath.points[nearestIndex] = selectedNode;
            newSelectedNodes.push(selectedNode);
        }

        this.selectedNodes = newSelectedNodes;

        // Mark for rebuild
        this.isBuilt = false;
    }

    /**
     * Select a point on the center path
     * @param {SketchPoint} point - Point to select
     */
    selectNode(point) {
        if (!this.selectedNodes.includes(point)) {
            this.selectedNodes.push(point);
        }
    }

    /**
     * Deselect a point
     * @param {SketchPoint} point - Point to deselect
     */
    deselectNode(point) {
        const index = this.selectedNodes.indexOf(point);
        if (index !== -1) {
            this.selectedNodes.splice(index, 1);
        }
    }

    /**
     * Clear all selected nodes
     */
    clearSelection() {
        this.selectedNodes = [];
    }

    /**
     * Find point near a position
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {number} threshold - Distance threshold
     * @returns {SketchPoint|null} Point if found, null otherwise
     */
    findPointNear(x, y, threshold = 10) {
        for (const point of this.centrePath.points) {
            const dist = Math.sqrt((point.x - x) ** 2 + (point.y - y) ** 2);
            if (dist < threshold) {
                return point;
            }
        }
        return null;
    }

    /**
     * Find a bezier control handle near the given coordinates
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {number} threshold - Distance threshold for detection
     * @returns {Object|null} - Object with {point, handle, index} or null if none found
     */
    findBezierHandleNear(x, y, threshold = 10) {
        for (let i = 0; i < this.centrePath.points.length; i++) {
            const point = this.centrePath.points[i];

            // Check controlPoint1 (outgoing handle)
            if (point.controlPoint1) {
                const dist1 = Math.sqrt(
                    (point.controlPoint1.x - x) ** 2 +
                    (point.controlPoint1.y - y) ** 2
                );
                if (dist1 < threshold) {
                    return {
                        point: point,
                        handle: 'controlPoint1',
                        handlePoint: point.controlPoint1,
                        index: i
                    };
                }
            }

            // Check controlPoint2 (incoming handle)
            if (point.controlPoint2) {
                const dist2 = Math.sqrt(
                    (point.controlPoint2.x - x) ** 2 +
                    (point.controlPoint2.y - y) ** 2
                );
                if (dist2 < threshold) {
                    return {
                        point: point,
                        handle: 'controlPoint2',
                        handlePoint: point.controlPoint2,
                        index: i
                    };
                }
            }
        }
        return null;
    }

    /**
     * Render the spline
     * @param {CanvasRenderingContext2D} ctx - Canvas context
     * @param {boolean} showControlPoints - Whether to show control points (e.g., when select tool is active)
     */
    render(ctx, showControlPoints = false) {
        if (!this.isBuilt && this.centrePath.size() > 0) {
            this.build();
        }

        // Render the outline path
        if (this.path.size() > 0) {
            ctx.save();
            ctx.strokeStyle = this.selected ? '#4CAF50' : '#333';
            ctx.fillStyle = 'rgba(76, 175, 80, 0.3)';
            ctx.lineWidth = 1;

            this.path.render(ctx);

            // Fill the path (holes as extra subpaths, evenodd rule - a
            // closed stroke is an annulus and must not fill its centre)
            ctx.beginPath();
            for (let i = 0; i < this.path.size(); i++) {
                const p = this.path.points[i];
                if (i === 0) {
                    ctx.moveTo(p.x, p.y);
                } else {
                    ctx.lineTo(p.x, p.y);
                }
            }
            ctx.closePath();
            for (const hole of this.path.holes || []) {
                for (let i = 0; i < hole.length; i++) {
                    const p = hole[i];
                    if (i === 0) ctx.moveTo(p.x, p.y);
                    else ctx.lineTo(p.x, p.y);
                }
                ctx.closePath();
            }
            ctx.fill('evenodd');

            ctx.restore();
        }

        // Show control points when select tool is active OR when spline is selected
        const shouldShowPoints = showControlPoints || this.selected;

        // Render center path and control points
        if (shouldShowPoints && this.centrePath.size() > 0) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
            ctx.lineWidth = 2;

            ctx.beginPath();
            for (let i = 0; i < this.centrePath.size(); i++) {
                const p = this.centrePath.points[i];
                if (i === 0) {
                    ctx.moveTo(p.x, p.y);
                } else {
                    ctx.lineTo(p.x, p.y);
                }
            }
            ctx.stroke();

            // Draw center points
            for (let i = 0; i < this.centrePath.size(); i++) {
                const p = this.centrePath.points[i];
                const isSelected = this.selectedNodes.includes(p);

                ctx.fillStyle = isSelected ? '#FFD700' : 'red'; // Gold for selected, red for normal
                ctx.beginPath();
                ctx.arc(p.x, p.y, isSelected ? 5 : 3, 0, Math.PI * 2);
                ctx.fill();

                // Draw selection ring
                if (isSelected) {
                    ctx.strokeStyle = '#FFD700';
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
                    ctx.stroke();
                }
            }

            ctx.restore();
        }
    }

    /**
     * Generate parallel slice planes along this spline
     * Creates planes perpendicular to the spline path at regular intervals
     * @param {number} spacing - Distance between planes in pixels
     * @param {number} start - Start position along path (0 to 1)
     * @param {number} end - End position along path (0 to 1)
     * @returns {Array<Object>} Array of plane data {position, normal, percent}
     */
    generateParallelPlanes(spacing = 50, start = 0, end = 1) {
        const planes = [];

        if (this.centrePath.size() < 2) {
            return planes;
        }

        const totalLength = this.getLength();
        const startDist = start * totalLength;
        const endDist = end * totalLength;
        const segmentLength = endDist - startDist;

        // Generate planes at spacing intervals
        const numPlanes = Math.max(1, Math.floor(segmentLength / spacing));
        const actualSpacing = segmentLength / numPlanes;

        for (let i = 0; i <= numPlanes; i++) {
            const dist = startDist + (i * actualSpacing);
            const percent = dist / totalLength;

            // Get position at this point
            const pos = this.getPos(percent);

            // Get tangent by looking slightly ahead and behind
            const delta = 0.001;
            const posBefore = this.getPos(Math.max(0, percent - delta));
            const posAfter = this.getPos(Math.min(1, percent + delta));

            // Calculate tangent (direction of curve)
            const tangent = new Vec2D(
                posAfter.x - posBefore.x,
                posAfter.y - posBefore.y
            );
            tangent.normalize();

            // Perpendicular to tangent in 2D (will become plane normal in 3D)
            // Rotate 90 degrees to get perpendicular
            const perpendicular = new Vec2D(-tangent.y, tangent.x);

            planes.push({
                position: pos,
                tangent: tangent,
                normal: perpendicular, // This is the 2D perpendicular, will be converted to 3D
                percent: percent,
                index: i
            });
        }

        return planes;
    }

    /**
     * Clone this spline
     * @returns {SketchSpline} Cloned spline
     */
    clone() {
        const newSpline = new SketchSpline(this.getParentSketch());
        newSpline.centrePath = this.centrePath.clone();
        newSpline.offsetSize = this.offsetSize;
        newSpline.offsetType = this.offsetType;
        newSpline.capType = this.capType;
        newSpline.joinType = this.joinType;
        newSpline.type = this.type; // Clone the type (e.g., TYPE_LEG)
        newSpline.centreOffset = new Map(this.centreOffset);
        newSpline.strokeId = this.strokeId; // Stable linked-stroke identity
        newSpline.build();
        return newSpline;
    }

    /**
     * Get bounds
     * @returns {Object} Bounds {minX, minY, maxX, maxY}
     */
    getBounds() {
        if (!this.isBuilt) {
            this.build();
        }
        return this.path.getBounds();
    }

    /**
     * Export to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        return {
            type: 'SketchSpline',
            strokeId: this.strokeId,
            splineType: this.type,
            centrePath: this.centrePath.toJSON(),
            offsetSize: this.offsetSize,
            offsetType: this.offsetType,
            capType: this.capType,
            joinType: this.joinType,
            autoSmooth: this.autoSmooth,
            centreOffset: Array.from(this.centreOffset.entries())
        };
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     * @param {SketchPlane} parentSketch - Parent sketch
     * @returns {SketchSpline} New spline instance
     */
    static fromJSON(data, parentSketch) {
        const spline = new SketchSpline(parentSketch);
        // NOTE: SketchPath.fromJSON signature is (parentSketch, data)
        spline.centrePath = SketchPath.fromJSON(parentSketch, data.centrePath);
        if (data.strokeId !== undefined) spline.strokeId = data.strokeId;
        if (data.splineType !== undefined) spline.setType(data.splineType);
        if (data.autoSmooth !== undefined) spline.autoSmooth = data.autoSmooth;
        spline.offsetSize = data.offsetSize || 20;
        spline.offsetType = data.offsetType || SketchSpline.OFFSET_BOTH;
        spline.capType = data.capType || SketchSpline.CAP_ROUND;
        spline.joinType = data.joinType || SketchSpline.JOIN_BEVEL;

        if (data.centreOffset) {
            spline.centreOffset = new Map(data.centreOffset);
        }

        spline.build();
        return spline;
    }
}
